import type { AxiosError, AxiosInstance } from 'axios';

import { Routes } from 'shared/routes';

export const SKIP_SESSION_CHECK_HEADER = 'X-Skip-Session-Check';

const CURRENT_USER_PATH = '/api/v1/current_user';

let axiosInstance: AxiosInstance | null = null;
let onSessionExpired: (() => void) | null = null;
let isRedirecting = false;
let verificationPromise: Promise<boolean> | null = null;

export function registerUnauthorizedAxios(instance: AxiosInstance): void {
  axiosInstance = instance;
}

export function setupUnauthorizedHandler(callbacks: { onSessionExpired: () => void }): void {
  onSessionExpired = callbacks.onSessionExpired;
}

/** Resets module state between tests. */
export function resetUnauthorizedHandlerState(): void {
  isRedirecting = false;
  verificationPromise = null;
}

function isCurrentUserUrl(url: string): boolean {
  return url.includes(CURRENT_USER_PATH);
}

async function verifySessionExpired(): Promise<boolean> {
  if (!axiosInstance) {
    return false;
  }

  if (!verificationPromise) {
    verificationPromise = axiosInstance
      .get(Routes.backend.apiV1CurrentUserPath(), {
        headers: { [SKIP_SESSION_CHECK_HEADER]: '1' },
      })
      .then(() => false)
      .catch((error: AxiosError) => error.response?.status === 401)
      .finally(() => {
        verificationPromise = null;
      });
  }

  return verificationPromise;
}

function triggerSessionExpired(): void {
  if (isRedirecting || !onSessionExpired) {
    return;
  }

  isRedirecting = true;
  onSessionExpired();
}

export async function handleUnauthorized(failedUrl: string): Promise<void> {
  if (isRedirecting) {
    return;
  }

  if (isCurrentUserUrl(failedUrl)) {
    triggerSessionExpired();
    return;
  }

  const expired = await verifySessionExpired();
  if (expired) {
    triggerSessionExpired();
  }
}
